'use client'

import { type StudioDay, type StudioWeekday, studioStarts } from '@repo/core/studio-calendar'
import { clockLabel } from '@repo/db/office-hours'
import {
  ArrowRight,
  AudioLines,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clapperboard,
  Headphones,
  Lightbulb,
  type LucideIcon,
  Mic,
  MonitorPlay,
  PackageCheck,
  Palette,
  Scissors,
  ScrollText,
  Smartphone,
  Sparkles,
  Video,
  Zap,
} from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { type CSSProperties, useEffect, useState } from 'react'
import {
  DAY_LONG,
  DAY_SHORT,
  MONTH,
  MONTH_OF,
  MONTH_SHORT,
  rowBasis,
  rowColumns,
  startsFor,
} from '@/components/sections/studio/studio-dates'
import { StudioDatesDialog } from '@/components/sections/studio/studio-dates-dialog.client'
import { creatorHref } from '@/lib/creator'
import { fromISO } from '@/lib/dates'
import { CONTAINER, SCREEN_FILL, SCREEN_TOP } from '@/lib/layout'
import { formatPLN, type StudioRoomView } from '@/lib/services'
import { type StudioPhoto, studioPhotos } from '@/lib/studio-photos'
import { KICKER } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * /pl/studio — one room at a time, the booking beside it, on one screen.
 *
 * ── WHAT THIS REPLACED, AND WHY ──────────────────────────────────────────
 * Two room cards side by side, a „Zarezerwuj termin" that unfolded a calendar
 * UNDER both of them, and the price, the block and the date spread over three
 * places on the page. The owner picked a different shape out of three drawn
 * proposals (Design canvas „Studio FLOU — 3 propozycje", proposal B, the one
 * he called *„przekonuje mnie najbardziej"*): the room is a large photograph
 * on the left, and everything you choose — studio, length, day, hour — sits
 * in a panel on the right with the total at the bottom of it.
 *
 * Every decision below is his, made one screen at a time against that
 * drawing; the ones that are not obvious from the code are quoted where they
 * land.
 *
 * ── ONE PAGE ─────────────────────────────────────────────────────────────
 * *„strona musi działać na zasadzie one page, czyli wszystko jest na jednym
 * ekranie bez przewijania w dół"*. From `lg` the grid is locked to the
 * screen (`SCREEN_LOCK`'s arithmetic) and the photograph is the part that
 * gives: it takes whatever height is left after the thumbnails and the „Po
 * nagraniu" cards, so the cards end where the „Przejdź do koszyka" button
 * ends. Below `lg` the page scrolls like any phone page and the total rides a
 * bar pinned to the bottom of the screen.
 */

/* ───────────────────────────── room look ───────────────────────────── */

/**
 * The page takes the colour of the room being booked.
 *
 * Owner: *„fajnie jakby tło było w kolorze studia które wybieramy czyli solo
 * niebieski, studio [Rozmowa] taki beżowy jasny"* — and then *„to samo ma być
 * z gradientami na przyciskach"*. So a room carries its own glow and its own
 * three gradients, and every accent on the page reads them from CSS variables
 * instead of from `--flou-*`. The header and the „My FLOU" button keep the
 * brand gradient: they belong to the site, not to the room.
 *
 * Keyed by slug and kept in code, the same call `BEST_FOR` made on the old
 * page: a colour is a design decision about two specific rooms, not a field
 * anybody should be editing in the panel. A room with no entry here falls back
 * to the brand's own gradient, so a third room still looks like FLOU.
 */
type RoomLook = {
  /** Two radial glows over a near-black ground. */
  ground: string
  grad: string
  gradText: string
  glow: string
  accent: string
}

const BRAND_LOOK: RoomLook = {
  ground:
    'radial-gradient(70% 55% at 30% 0%, oklch(0.62 0.26 330 / 0.28) 0%, oklch(0.45 0.2 300 / 0.1) 45%, transparent 75%), oklch(0.08 0.012 300 / 0.6)',
  grad: 'var(--flou-gradient)',
  gradText: 'var(--flou-gradient-text)',
  glow: 'oklch(0.62 0.26 330 / 0.35)',
  accent: 'oklch(0.66 0.25 320)',
}

const ROOM_LOOK: Record<string, RoomLook> = {
  rozmowa: {
    ground:
      'radial-gradient(70% 55% at 30% 0%, oklch(0.82 0.06 75 / 0.3) 0%, oklch(0.6 0.05 60 / 0.12) 45%, transparent 75%), radial-gradient(50% 40% at 90% 30%, oklch(0.78 0.05 80 / 0.14) 0%, transparent 70%), oklch(0.1 0.01 70 / 0.82)',
    grad: 'linear-gradient(100deg, oklch(0.9 0.04 85), oklch(0.8 0.07 72) 45%, oklch(0.7 0.08 58))',
    gradText:
      'linear-gradient(100deg, oklch(0.9 0.04 85), oklch(0.82 0.07 72) 45%, oklch(0.76 0.08 58))',
    glow: 'oklch(0.8 0.07 75 / 0.35)',
    accent: 'oklch(0.78 0.08 72)',
  },
  solo: {
    ground:
      'radial-gradient(70% 55% at 30% 0%, oklch(0.6 0.19 255 / 0.4) 0%, oklch(0.45 0.16 260 / 0.16) 45%, transparent 75%), radial-gradient(50% 40% at 90% 30%, oklch(0.62 0.2 250 / 0.2) 0%, transparent 70%), oklch(0.08 0.02 260 / 0.82)',
    grad: 'linear-gradient(100deg, oklch(0.8 0.12 225), oklch(0.66 0.18 252) 45%, oklch(0.58 0.2 268))',
    gradText:
      'linear-gradient(100deg, oklch(0.82 0.11 225), oklch(0.76 0.14 248) 45%, oklch(0.74 0.14 268))',
    glow: 'oklch(0.6 0.2 255 / 0.4)',
    accent: 'oklch(0.64 0.19 252)',
  },
}

function roomLook(slug: string): RoomLook {
  return ROOM_LOOK[slug] ?? BRAND_LOOK
}

/** The variables every accent below reads. */
function lookVars(look: RoomLook): CSSProperties {
  return {
    ['--studio-grad' as string]: look.grad,
    ['--studio-grad-text' as string]: look.gradText,
    ['--studio-glow' as string]: look.glow,
    ['--studio-accent' as string]: look.accent,
  }
}

const GRAD_TEXT = 'bg-[image:var(--studio-grad-text)] bg-clip-text text-transparent'

/* ───────────────────────────── features ────────────────────────────── */

/**
 * What every room comes with, whatever the catalogue lists for it.
 *
 * Owner: *„po 6 ikon przy każdym studiu opisujących co posiada, jakie są
 * zalety"* and *„w każdej zalecie (…) jeden musi stanowić technik, który
 * obsługuje nagrania"*. The catalogue carries four lines per room, written per
 * room; these two are true of both, so they are added here rather than copied
 * into two rows of seed data that would then have to be kept in step. A room
 * whose catalogue already says it (matched loosely, the way the icons are) does
 * not get it twice.
 */
const SHARED_FEATURES = ['W pełni wyposażone studio', 'Technik prowadzi nagranie od A do Z']

function roomFeatures(room: StudioRoomView): string[] {
  const own = room.features
  const extra = SHARED_FEATURES.filter((line) => {
    const head = line.split(' ')[0]?.toLowerCase() ?? ''
    return !own.some((mine) => mine.toLowerCase().includes(head))
  })
  return [...own, ...extra]
}

/**
 * A glyph per feature line, matched by a pattern on the label rather than by
 * a key — the rows are editable in the panel and a renamed key would silently
 * empty the list. A label nothing matches gets a neutral spark, never nothing.
 *
 * The full sentence is shown beside it, not a two-word short form: on the
 * photograph there is room for it, and the owner's six lines were written to
 * be read („Mikrofony na wysięgnikach, dźwięk bez kompromisów").
 */
const FEATURE_ICONS: readonly { pattern: RegExp; Icon: LucideIcon }[] = [
  { pattern: /teleprompter/i, Icon: ScrollText },
  { pattern: /podgl[ąa]d/i, Icon: MonitorPlay },
  { pattern: /kamer/i, Icon: Video },
  { pattern: /mikrofon/i, Icon: Mic },
  { pattern: /scenograf/i, Icon: Palette },
  { pattern: /re[żz]yser/i, Icon: Clapperboard },
  { pattern: /technik/i, Icon: Headphones },
  { pattern: /kadr|pionow/i, Icon: Smartphone },
  { pattern: /jeden strza[łl]|rozstawiani/i, Icon: Zap },
  { pattern: /monta[żz]/i, Icon: Scissors },
  { pattern: /wyposa[żz]on/i, Icon: PackageCheck },
  { pattern: /o[śs]wietlenie|[śs]wiat[łl]o/i, Icon: Lightbulb },
  { pattern: /audio|d[źz]wi[ęe]k/i, Icon: AudioLines },
]

function featureIcon(label: string): LucideIcon {
  return FEATURE_ICONS.find((entry) => entry.pattern.test(label))?.Icon ?? Sparkles
}

/* ───────────────────────────── after the shoot ─────────────────────── */

/**
 * Owner: *„w opcji B daj ten segment, że nie kończymy na wynajmie"*, then, when
 * the room description under the photograph went: *„w to miejsce dajemy «nie
 * kończysz na oddaniu plików» i te 3 punkty"*. The full sentences stay — *„pod
 * zdjęciem jest za mało tekstu, to przywracamy co tam było"*.
 */
const AFTER = [
  {
    title: 'Montaż dogadany na miejscu',
    body: 'Zaraz po nagraniu siadamy w biurze obok i ustalamy, co z tego ma powstać, długość, tempo, format.',
  },
  {
    title: 'Harmonogram publikacji',
    body: 'Rozpisujemy, co i kiedy wychodzi. Jeden dzień zdjęciowy potrafi wykarmić kilka tygodni contentu.',
  },
  {
    title: 'Od A do Z',
    body: 'Miniatury, opisy, shortsy z długiej formy. Wychodzisz z gotowym planem, nie z folderem surówek.',
  },
]

/* ───────────────────────────── prices ──────────────────────────────── */

/**
 * The room's floor, said the way the old card said it: the cheapest HOUR the
 * room sells (owner: *„pokazujemy od najniższej"*), the cheapest block when
 * nothing is priced by a window, and the plain fact when nothing is priced.
 */
function roomFrom(room: StudioRoomView): string {
  if (room.fromHourlyNet !== null) return `od ${formatPLN(Math.round(room.fromHourlyNet))} / h`
  const priced = room.plans.filter((plan) => !plan.quoteOnly)
  if (priced.length === 0) return 'Wycena indywidualna'
  const low = priced.reduce((a, b) => (b.priceNet < a.priceNet ? b : a))
  return `od ${low.price}`
}

/* ───────────────────────────── calendar ────────────────────────────── */

/**
 * Which days a block of this length can start on, and at what hour.
 *
 * From `room.calendar`, computed on the server: the PUBLISHED calendar (spec
 * §10 · I1, *„klient rezerwuje tylko z wpisanych godzin"*) minus what is
 * already reserved. The last start is closing time minus the block, so a day
 * too short for the block — 8 h on a Friday that runs 10–18 — is simply not
 * offered.
 */
/**
 * The opening hours, said once — under the total, and in the dates dialog.
 *
 * Built from the week the SERVER read out of the published calendar, never
 * from what is free: a booked Monday morning would otherwise rewrite it into
 * „pn 9:00–20:00" and announce a closing time the studio never published.
 * Runs of identical days fold together, „pn–czw 8:00–20:00".
 */
function openHoursNote(week: readonly StudioWeekday[]): string | undefined {
  if (week.length === 0) return undefined
  const runs = week.reduce<{ from: number; to: number; opensAt: number; closesAt: number }[]>(
    (acc, day) => {
      const last = acc.at(-1)
      if (
        last &&
        last.opensAt === day.opensAt &&
        last.closesAt === day.closesAt &&
        last.to === day.weekday - 1
      ) {
        last.to = day.weekday
        return acc
      }
      acc.push({ from: day.weekday, to: day.weekday, opensAt: day.opensAt, closesAt: day.closesAt })
      return acc
    },
    [],
  )
  return runs
    .map((run) => {
      const days =
        run.from === run.to
          ? (DAY_SHORT[run.from] ?? '')
          : `${DAY_SHORT[run.from] ?? ''}–${DAY_SHORT[run.to] ?? ''}`
      return `${days} ${clockLabel(run.opensAt)}–${clockLabel(run.closesAt)}`
    })
    .join(' · ')
}

/** Days with at least one start left for a block this long. */
function openDaysFor(calendar: readonly StudioDay[], hours: number): string[] {
  return calendar.filter((day) => studioStarts(day, hours).length > 0).map((day) => day.date)
}

/** How many days the strip in the panel shows. The rest live in „Wszystkie terminy". */
const STRIP_DAYS = 6

/* ───────────────────────────── page ────────────────────────────────── */

/** How long one photograph stays up in the slideshow. */
const SLIDE_MS = 7000

export function StudioPageView({ locale, rooms }: { locale: string; rooms: StudioRoomView[] }) {
  const [roomSlug, setRoomSlug] = useState(rooms[0]?.slug ?? '')
  const [blockSlug, setBlockSlug] = useState<string | null>(null)
  const [pickedDay, setPickedDay] = useState<string | null>(null)
  const [pickedTime, setPickedTime] = useState<string | null>(null)
  /** First day of the six in the strip. Moves when a far date is picked. */
  const [stripFrom, setStripFrom] = useState<string | null>(null)
  const [photo, setPhoto] = useState(0)
  const [datesOpen, setDatesOpen] = useState(false)

  const room = rooms.find((one) => one.slug === roomSlug) ?? rooms[0]
  const photos = room ? roomPhotos(room) : []

  // ── THE SLIDESHOW ───────────────────────────────────────────────────
  // Owner: *„niech zdjęcia przesuwają się ładnie same, co x sekund, nie zbyt
  // szybko, żeby to był fajny pokaz slajdów"*. A timeout rather than an
  // interval, keyed on the photograph: every change — automatic, an arrow, a
  // thumbnail — starts the count again, so a picture somebody just chose is
  // never swapped out from under them a second later. It holds still while
  // the dates dialog is open and for anyone who asked the system for less
  // motion.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `photo` restarts the count on every change of picture, by design
  useEffect(() => {
    if (photos.length < 2 || datesOpen) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const timer = window.setTimeout(() => setPhoto((at) => (at + 1) % photos.length), SLIDE_MS)
    return () => window.clearTimeout(timer)
  }, [photo, photos.length, datesOpen])

  if (!room) return null

  const look = roomLook(room.slug)
  const features = roomFeatures(room)

  const block = room.blocks.find((one) => one.slug === blockSlug) ?? room.blocks[0] ?? null
  const hours = block?.hours ?? 1
  const openDays = openDaysFor(room.calendar, hours)

  // The day and the hour fall back to the first free ones, so the panel never
  // shows a total for nothing and the button never asks „najpierw wybierz".
  const day = pickedDay && openDays.includes(pickedDay) ? pickedDay : (openDays[0] ?? null)
  const times = startsFor(room.calendar, day, hours)
  const time = pickedTime && times.includes(pickedTime) ? pickedTime : (times[0] ?? null)

  const soonestDay = openDays[0] ?? null
  const soonestTime = startsFor(room.calendar, soonestDay, hours)[0] ?? null

  const stripStart = Math.max(0, stripFrom ? openDays.indexOf(stripFrom) : 0)
  const strip = openDays.slice(stripStart, stripStart + STRIP_DAYS)

  const pickRoom = (slug: string) => {
    if (slug === room.slug) return
    setRoomSlug(slug)
    setBlockSlug(null)
    setPhoto(0)
    setStripFrom(null)
    setPickedTime(null)
  }

  const pickDay = (iso: string, at: string | null = null) => {
    setPickedDay(iso)
    setPickedTime(at)
    // A day outside the six on screen becomes the first of them, so what was
    // just chosen is always visible and always the white one.
    const at6 = openDays.indexOf(iso)
    if (at6 < stripStart || at6 >= stripStart + STRIP_DAYS) setStripFrom(iso)
  }

  const showPhoto = (to: number) => {
    if (photos.length === 0) return
    setPhoto(((to % photos.length) + photos.length) % photos.length)
  }

  const href =
    day && time
      ? creatorHref(locale, {
          offeringSlug: room.slug,
          ...(block ? { variantSlug: block.slug } : {}),
          from: day,
          time,
        })
      : null

  const dayDate = day ? fromISO(day) : null
  const summary = [
    room.name,
    block?.name,
    dayDate
      ? `${DAY_SHORT[dayDate.getDay()]} ${dayDate.getDate()} ${MONTH_SHORT[dayDate.getMonth()]}${time ? `, ${time}` : ''}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ')
  const hoursNote = openHoursNote(room.week)

  const soonestDate = soonestDay ? fromISO(soonestDay) : null
  const stripMonth = fromISO(strip[0] ?? day ?? '')

  return (
    <div className="relative w-full overflow-x-clip" style={lookVars(look)}>
      {/* THE ROOM'S COLOUR, under everything. One layer per room, cross-faded,
          because a `background` cannot be transitioned between gradients. */}
      <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-0">
        {rooms.map((one) => (
          <div
            key={one.slug}
            className="absolute inset-0 transition-opacity duration-700 ease-out"
            style={{
              background: roomLook(one.slug).ground,
              opacity: one.slug === room.slug ? 1 : 0,
            }}
          />
        ))}
      </div>

      <div
        className={cn(
          CONTAINER,
          SCREEN_TOP,
          'relative z-10 pb-36 lg:pb-[clamp(0.75rem,3svh,2.5rem)]',
        )}
      >
        <div
          className={cn(
            SCREEN_FILL,
            'lg:h-[calc(100svh_-_clamp(5rem,10svh,8rem)_-_clamp(0.75rem,3svh,2.5rem))] lg:min-h-[44rem]',
          )}
        >
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-[minmax(0,1fr)_minmax(24rem,27.5rem)] lg:grid-rows-[minmax(0,1fr)_auto_auto] lg:gap-y-3">
            {/* ── THE PHOTOGRAPH ─────────────────────────────── */}
            <section
              aria-label={room.name}
              className="relative h-[27.5rem] overflow-hidden rounded-3xl border border-white/[0.12] lg:col-start-1 lg:row-start-1 lg:h-auto lg:min-h-[21rem]"
            >
              {photos.map((one, index) => (
                <Image
                  key={one.src}
                  src={one.src}
                  alt={index === photo ? one.alt : ''}
                  aria-hidden={index === photo ? undefined : true}
                  fill
                  priority={index === 0}
                  sizes="(max-width: 1024px) 100vw, 65vw"
                  className={cn(
                    'object-cover',
                    index === photo ? 'scale-100 opacity-100' : 'scale-[1.06] opacity-0',
                  )}
                  style={{
                    transition:
                      'opacity 1800ms cubic-bezier(0.4,0,0.2,1), transform 9000ms cubic-bezier(0.2,0,0.2,1)',
                  }}
                />
              ))}
              {photos.length === 0 ? (
                <div className="absolute inset-0 flex items-center justify-center bg-white/[0.04] text-white/40">
                  <Clapperboard size={28} aria-hidden="true" />
                </div>
              ) : null}

              {/* The name and the six features belong to the FIRST photograph.
                  Owner: *„te 6 atutów jest tylko na 1 zdjęciu, jak przewiniemy
                  na kolejne, to ładnie automatycznie się chowa"* — *„delikatnie,
                  powolutku"* — and the name goes up the way the features go
                  down. The second picture is shown whole. */}
              <div
                aria-hidden="true"
                className={cn(FADE, photo === 0 ? 'opacity-100' : 'opacity-0')}
                style={{
                  background:
                    'linear-gradient(to bottom, oklch(0.08 0.012 300 / 0.88) 0%, transparent 40%)',
                }}
              />
              <div
                aria-hidden="true"
                className={cn(FADE, 'hidden lg:block', photo === 0 ? 'opacity-100' : 'opacity-0')}
                style={{
                  background:
                    'linear-gradient(to top, oklch(0.06 0.01 280 / 0.92) 0%, oklch(0.06 0.01 280 / 0.6) 28%, transparent 52%)',
                }}
              />

              <div
                className={cn(
                  'absolute inset-x-5 top-5 flex flex-wrap items-start justify-between gap-3 transition-[opacity,transform] duration-[1600ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none lg:inset-x-7 lg:top-6',
                  photo === 0
                    ? 'translate-y-0 opacity-100'
                    : 'pointer-events-none -translate-y-1.5 opacity-0',
                )}
              >
                <div>
                  <p className={cn(KICKER, 'text-white/75')}>{room.kicker}</p>
                  <h1 className="mt-1.5 font-masthead text-[2.6rem] font-bold leading-[0.95] tracking-[-0.035em] text-white lg:text-[3.4rem]">
                    {room.name}
                  </h1>
                  <p className="mt-2 text-[0.9rem] text-white/85 lg:text-[1rem]">{room.tagline}</p>
                </div>
                <span className="rounded-full border border-white/[0.14] bg-white/[0.07] px-4 py-2 text-[0.8rem] font-semibold text-white backdrop-blur-xl">
                  {room.capacityLabel}
                </span>
              </div>

              <ul
                aria-label="Co jest w studiu"
                className={cn(
                  'absolute inset-x-6 bottom-6 hidden grid-cols-3 gap-x-4 gap-y-3.5 transition-[opacity,transform] duration-[1600ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none lg:grid',
                  photo === 0
                    ? 'translate-y-0 opacity-100'
                    : 'pointer-events-none translate-y-1.5 opacity-0',
                )}
              >
                {features.map((line) => (
                  <FeatureItem key={line} label={line} />
                ))}
              </ul>

              {/* Arrows. Owner: *„strzałki takie minimalistyczne do przesuwania
                  zdjęć"*. */}
              {photos.length > 1 ? (
                <>
                  <button
                    type="button"
                    onClick={() => showPhoto(photo - 1)}
                    aria-label="Poprzednie zdjęcie"
                    className={cn(ARROW, 'left-3 lg:left-5')}
                  >
                    <ChevronLeft size={18} strokeWidth={1.6} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => showPhoto(photo + 1)}
                    aria-label="Następne zdjęcie"
                    className={cn(ARROW, 'right-3 lg:right-5')}
                  >
                    <ChevronRight size={18} strokeWidth={1.6} aria-hidden="true" />
                  </button>
                  {/* Dots on a phone, where thumbnails would be a second row of
                      pictures before the first word about booking. */}
                  <div className="absolute inset-x-0 bottom-4 flex justify-center gap-1.5 lg:hidden">
                    {photos.map((one, index) => (
                      <button
                        key={one.src}
                        type="button"
                        onClick={() => showPhoto(index)}
                        aria-label={`Zdjęcie ${index + 1}`}
                        aria-current={index === photo}
                        className={cn(
                          'h-[7px] rounded-full transition-all duration-300',
                          index === photo ? 'w-[22px] bg-white' : 'w-[7px] bg-white/45',
                        )}
                      />
                    ))}
                  </div>
                </>
              ) : null}
            </section>

            {/* Features under the photograph on a phone: on 390px six of them
                over the picture would cover the room they describe. */}
            <ul
              aria-label="Co jest w studiu"
              className="grid grid-cols-2 gap-x-3 gap-y-3.5 px-1 pt-1 lg:hidden"
            >
              {features.map((line) => (
                <FeatureItem key={line} label={line} compact />
              ))}
            </ul>

            {/* ── THUMBNAILS (desktop) ───────────────────────── */}
            {photos.length > 1 ? (
              <div className="hidden gap-2.5 lg:col-start-1 lg:row-start-2 lg:flex">
                {photos.map((one, index) => (
                  <button
                    key={one.src}
                    type="button"
                    onClick={() => showPhoto(index)}
                    aria-label={`Zdjęcie ${index + 1}`}
                    aria-current={index === photo}
                    className={cn(
                      'relative aspect-[16/10] w-[5.75rem] overflow-hidden rounded-xl border-2 transition-opacity',
                      index === photo
                        ? 'border-white opacity-100'
                        : 'border-transparent opacity-60 hover:opacity-90',
                    )}
                  >
                    <Image src={one.src} alt="" fill sizes="92px" className="object-cover" />
                  </button>
                ))}
              </div>
            ) : null}

            {/* ── THE BOOKING PANEL ──────────────────────────── */}
            <aside
              aria-label="Rezerwacja"
              className="flex min-h-0 min-w-0 flex-col gap-4 rounded-3xl border border-white/[0.12] bg-white/[0.05] p-4 backdrop-blur-xl lg:col-start-2 lg:row-span-3 lg:row-start-1 lg:overflow-y-auto lg:p-5 lg:pb-0"
            >
              {/* SOONEST. Owner: *„brakuje mi gdzieś karty z info o
                  najbliższym możliwym terminie"*, then *„to może inaczej"* and
                  *„usuwamy tą zieloną kropkę"*. The whole card is the button;
                  the white tile is the same white the picked day wears. */}
              {soonestDay && soonestDate ? (
                <button
                  type="button"
                  onClick={() => pickDay(soonestDay, soonestTime)}
                  aria-label={`Wybierz najbliższy wolny termin: ${DAY_LONG[soonestDate.getDay()]} ${soonestDate.getDate()} ${MONTH_OF[soonestDate.getMonth()]}${soonestTime ? `, ${soonestTime}` : ''}`}
                  className="flex w-full items-center gap-3.5 rounded-[1.1rem] border border-white/[0.12] bg-white/[0.04] py-2.5 pr-3 pl-2.5 text-left transition-colors hover:border-white/30"
                >
                  <span
                    aria-hidden="true"
                    className="flex size-14 shrink-0 flex-col items-center justify-center rounded-[0.9rem] bg-white text-neutral-900"
                  >
                    <small className="text-[0.66rem] font-semibold uppercase tracking-[0.08em] opacity-70">
                      {DAY_SHORT[soonestDate.getDay()]}
                    </small>
                    <b className="font-masthead text-[1.4rem] leading-none">
                      {soonestDate.getDate()}
                    </b>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={cn(KICKER, 'block text-[0.68rem] text-white/50')}>
                      Najbliższy wolny
                    </span>
                    <span className="mt-1 block font-masthead text-[1.15rem] font-bold capitalize text-white">
                      {DAY_LONG[soonestDate.getDay()]}
                      {soonestTime ? `, ${soonestTime}` : ''}
                    </span>
                  </span>
                  <span
                    aria-hidden="true"
                    className="inline-flex size-[2.4rem] shrink-0 items-center justify-center rounded-full border border-white/20 text-white"
                  >
                    <ArrowRight size={16} />
                  </span>
                </button>
              ) : null}

              {/* STUDIO. White ring, no glow — owner: *„paski wokół studio oraz
                  ile godzin niech będą białe bez glow"*. */}
              {rooms.length > 1 ? (
                <div>
                  <p className={cn(KICKER, 'mb-2 text-white/50')}>Studio</p>
                  <div className="flex gap-2.5">
                    {rooms.map((one) => {
                      const on = one.slug === room.slug
                      const cover = roomPhotos(one)[0]
                      return (
                        <button
                          key={one.slug}
                          type="button"
                          onClick={() => pickRoom(one.slug)}
                          aria-pressed={on}
                          className={cn(OPTION, 'flex-1 gap-3 p-2', on ? OPTION_ON : OPTION_OFF)}
                        >
                          {cover ? (
                            <span className="relative size-11 shrink-0 overflow-hidden rounded-[0.6rem]">
                              <Image
                                src={cover.src}
                                alt=""
                                fill
                                sizes="44px"
                                className="object-cover"
                              />
                            </span>
                          ) : null}
                          <span className="min-w-0 text-left">
                            <span className="block font-masthead text-[1.1rem] font-bold leading-tight">
                              {one.name}
                            </span>
                            <span className="block text-[0.75rem] text-white/50">
                              {roomFrom(one)}
                            </span>
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              ) : null}

              {/* HOW LONG. One under the other — owner: *„muszą być jedna pod
                  drugą, nie dwie obok siebie"* — and, on a desktop, as tall as
                  the panel allows: *„paski ile godzin muszą być na wysokość
                  większe, żeby cały ten konfigurator był zapełniony"*.

                  NO `min-h-0` HERE, however much the rest of this rail uses it.
                  `flex-1` gives this block a base size of ZERO, so it is handed
                  nothing but the rail's leftover space; `min-h-0` then lets it
                  shrink under that, while the buttons inside keep their own
                  `min-h`. The block ends up 106px tall around 310px of content,
                  and because nothing here clips, the bars paint straight over
                  the day picker and the start times below them. With the
                  default `min-height: auto` the block never goes under its
                  content, the bars still grow to fill a tall panel, and a rail
                  that runs out of room scrolls on its own `overflow-y-auto`. */}
              {room.blocks.length > 0 ? (
                <div className="flex flex-col lg:flex-1">
                  <p className={cn(KICKER, 'mb-2 text-white/50')}>Ile godzin?</p>
                  <div className="flex flex-col gap-1.5 lg:flex-1">
                    {room.blocks.map((one) => {
                      const on = one.slug === block?.slug
                      return (
                        <button
                          key={one.slug}
                          type="button"
                          onClick={() => setBlockSlug(one.slug)}
                          aria-pressed={on}
                          className={cn(
                            OPTION,
                            'min-h-12 justify-between px-4 lg:max-h-16 lg:min-h-[2.65rem] lg:flex-1 lg:basis-0',
                            on ? OPTION_ON : OPTION_OFF,
                          )}
                        >
                          <span className="text-[0.88rem] font-semibold">{one.name}</span>
                          <span className="font-masthead text-[1rem] font-bold">{one.price}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              ) : null}

              {/* DAY. Six free days, and the rest one tap away — owner: *„nie
                  można wybrać terminu na za 2 tygodnie, bo jest tylko 6 dat"*,
                  then *„zamiast cały kalendarz daj wszystkie terminy"*. */}
              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <p className={cn(KICKER, 'text-white/50')}>
                    Dzień{stripMonth ? ` · ${MONTH[stripMonth.getMonth()]}` : ''}
                  </p>
                  {openDays.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => setDatesOpen(true)}
                      aria-haspopup="dialog"
                      className="inline-flex items-center gap-1.5 py-1 text-[0.78rem] font-semibold text-white/80 transition-colors hover:text-white"
                    >
                      <CalendarDays size={15} strokeWidth={1.7} aria-hidden="true" />
                      Wszystkie terminy
                    </button>
                  ) : null}
                </div>
                {strip.length > 0 ? (
                  <div className="flex gap-1.5">
                    {strip.map((iso) => {
                      const date = fromISO(iso)
                      const on = iso === day
                      return (
                        <button
                          key={iso}
                          type="button"
                          onClick={() => pickDay(iso)}
                          aria-pressed={on}
                          aria-label={
                            date
                              ? `${DAY_LONG[date.getDay()]} ${date.getDate()} ${MONTH_OF[date.getMonth()]}`
                              : iso
                          }
                          className={cn(
                            'flex h-[3.25rem] min-w-0 flex-1 flex-col items-center justify-center gap-px rounded-xl border transition-colors',
                            on
                              ? 'border-white bg-white text-neutral-900'
                              : 'border-white/[0.12] bg-white/[0.03] text-white hover:border-white/30',
                          )}
                        >
                          <small
                            className={cn(
                              'text-[0.68rem] uppercase tracking-[0.08em]',
                              on ? 'text-neutral-900/70' : 'text-white/50',
                            )}
                          >
                            {date ? DAY_SHORT[date.getDay()] : ''}
                          </small>
                          <b className="font-masthead text-[1.2rem] leading-none">
                            {date?.getDate()}
                          </b>
                        </button>
                      )
                    })}
                  </div>
                ) : (
                  <p className="text-[0.85rem] leading-relaxed text-white/60">
                    Kalendarz jest na razie pełny.{' '}
                    <Link
                      href={`/${locale}/kontakt`}
                      className="font-semibold text-white underline"
                    >
                      Napisz do nas
                    </Link>
                    , znajdziemy termin.
                  </p>
                )}
              </div>

              {/* START. *„Zamiast start niech będzie godzina rozpoczęcia"*. */}
              {times.length > 0 ? (
                <div>
                  <p className={cn(KICKER, 'mb-2 text-white/50')}>Godzina rozpoczęcia</p>
                  <div className="flex flex-wrap gap-2">
                    {times.map((at) => {
                      const on = at === time
                      return (
                        <button
                          key={at}
                          type="button"
                          onClick={() => setPickedTime(at)}
                          aria-pressed={on}
                          style={rowBasis(rowColumns(times.length, 4), 0.5)}
                          className={cn(
                            'min-h-11 min-w-0 grow whitespace-nowrap rounded-full border px-2 text-[0.92rem] font-semibold tabular-nums transition-colors',
                            on
                              ? 'border-white bg-white text-neutral-900'
                              : 'border-white/[0.14] text-white/85 hover:border-white/35',
                          )}
                        >
                          {at}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ) : null}

              {/* TOTAL. In the panel on a desktop; on a phone it rides the bar
                  at the bottom of the screen instead (below).

                  STUCK TO THE FOOT OF THE RAIL, because the rail scrolls. Six
                  durations, a day picker and fourteen start times do not fit
                  825px of panel, so the price and the button were simply below
                  the rail's own fold — the one control the whole configurator
                  exists to reach, reachable only by scrolling a pane most
                  people would not notice scrolls. `mt-auto` still parks it at
                  the bottom when the content is short; the bleed and the fill
                  are there so the options do not show through it on the way
                  past. */}
              <div className="mt-auto hidden flex-col gap-2.5 border-t border-white/10 pt-3.5 lg:sticky lg:bottom-0 lg:z-10 lg:-mx-5 lg:flex lg:bg-[oklch(0.12_0.018_300)]/95 lg:px-5 lg:pb-5 lg:backdrop-blur-xl">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0 text-[0.85rem] text-white/65">{summary}</span>
                  <span className="shrink-0">
                    <span className="font-masthead text-[2rem] font-bold tracking-[-0.02em] text-white">
                      {block?.price ?? '—'}
                    </span>{' '}
                    <span className="text-[0.8rem] text-white/50">netto</span>
                  </span>
                </div>
                <CartButton href={href} label="Przejdź do koszyka" />
                {hoursNote ? (
                  <p className="text-[0.75rem] leading-normal text-white/50">{hoursNote}</p>
                ) : null}
              </div>
            </aside>

            {/* ── AFTER THE SHOOT ────────────────────────────── */}
            <section
              aria-labelledby="po-nagraniu"
              className="pt-6 lg:col-start-1 lg:row-start-3 lg:pt-0"
            >
              <div className="mb-3 flex flex-col gap-1 lg:flex-row lg:items-baseline lg:gap-3.5">
                <p className={cn(KICKER, 'text-white/50')}>Po nagraniu</p>
                <h2
                  id="po-nagraniu"
                  className="font-masthead text-[1.6rem] font-bold leading-[1.1] tracking-[-0.02em] text-white"
                >
                  Nie kończymy na <span className={GRAD_TEXT}>oddaniu plików.</span>
                </h2>
              </div>
              <ol className="grid grid-cols-1 gap-2.5 md:grid-cols-3 lg:gap-3">
                {AFTER.map((step, index) => (
                  <li
                    key={step.title}
                    className="flex flex-col gap-2 rounded-[1.25rem] border border-white/[0.12] bg-white/[0.05] px-[1.125rem] py-4 backdrop-blur-xl"
                  >
                    <span
                      aria-hidden="true"
                      className={cn(GRAD_TEXT, 'font-masthead text-[2rem] font-bold leading-none')}
                    >
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <h3 className="font-heading text-[0.95rem] font-bold text-white">
                      {step.title}
                    </h3>
                    <p className="text-[0.82rem] leading-normal text-white/65">{step.body}</p>
                  </li>
                ))}
              </ol>
              {hoursNote ? (
                <p className="mt-5 text-center text-[0.75rem] text-white/50 lg:hidden">
                  {hoursNote}
                </p>
              ) : null}
            </section>
          </div>
        </div>
      </div>

      {/* ── THE TOTAL ON A PHONE ───────────────────────────────
          Pinned to the bottom so the price and the button are never a scroll
          away. It stops short of the right edge: the assistant's launcher
          lives in that corner. */}
      <div className="fixed bottom-3 left-3 right-20 z-30 flex items-center gap-3 rounded-[1.4rem] border border-white/[0.14] bg-[oklch(0.12_0.01_280/0.85)] py-2.5 pr-2.5 pl-4 backdrop-blur-xl lg:hidden">
        <div className="min-w-0 flex-1">
          <p className="truncate text-[0.72rem] text-white/65">{summary}</p>
          <p className="mt-0.5">
            <span className="font-masthead text-[1.35rem] font-bold tracking-[-0.02em] text-white">
              {block?.price ?? '—'}
            </span>{' '}
            <span className="text-[0.72rem] text-white/50">netto</span>
          </p>
        </div>
        <CartButton href={href} label="Do koszyka" compact />
      </div>

      {datesOpen ? (
        <StudioDatesDialog
          onClose={() => setDatesOpen(false)}
          roomName={room.name}
          calendar={room.calendar}
          week={room.week}
          hours={hours}
          openDays={openDays}
          value={day}
          time={time}
          hoursNote={hoursNote}
          onPick={(iso, at) => {
            pickDay(iso, at)
            setDatesOpen(false)
          }}
        />
      ) : null}
    </div>
  )
}

/* ───────────────────────────── pieces ──────────────────────────────── */

const FADE =
  'pointer-events-none absolute inset-0 transition-opacity duration-[1400ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none'

const ARROW =
  'absolute top-1/2 inline-flex size-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-black/25 text-white backdrop-blur-md transition-colors hover:border-white/60 hover:bg-black/45'

const OPTION = 'flex items-center rounded-[0.9rem] border text-white transition-colors duration-200'
const OPTION_ON = 'border-white bg-white/[0.06]'
const OPTION_OFF = 'border-white/[0.12] bg-white/[0.03] hover:border-white/30'

/**
 * One feature line: a plain icon in a dark, thin-ringed square, and the
 * sentence. Owner, after a gradient version: *„teraz zbyt bajkowo, ma być
 * prosto, minimalistycznie"*.
 */
function FeatureItem({ label, compact = false }: { label: string; compact?: boolean }) {
  const Icon = featureIcon(label)
  return (
    <li
      className={cn(
        'flex items-center gap-3 font-medium leading-[1.3] text-white',
        compact ? 'text-[0.8rem]' : 'text-[0.86rem]',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-[0.6rem] border border-white/35 bg-black/35 backdrop-blur-md',
          compact ? 'size-[2.1rem]' : 'size-[2.4rem]',
        )}
      >
        <Icon size={compact ? 16 : 17} strokeWidth={1.8} />
      </span>
      {label}
    </li>
  )
}

/**
 * „Przejdź do koszyka" — white, black type, the same white as the picked day.
 * Owner: *„ma być to biały przycisk i czarny napis, identycznie z wyborem
 * dnia"*. It opens the creator with the room, the block, the day and the hour
 * already filled in; the creator puts it in the basket.
 */
function CartButton({
  href,
  label,
  compact = false,
}: {
  href: string | null
  label: string
  compact?: boolean
}) {
  const shape = cn(
    'inline-flex shrink-0 items-center justify-center gap-2 rounded-full font-semibold',
    compact ? 'min-h-12 px-[1.125rem] text-[0.9rem]' : 'min-h-12 w-full',
  )
  if (!href) {
    return (
      <span className={cn(shape, 'bg-white/10 text-white/50')} aria-disabled="true">
        {label}
      </span>
    )
  }
  return (
    <Link
      href={href}
      className={cn(
        shape,
        'bg-white text-neutral-900 transition-transform duration-300 hover:scale-[1.02]',
      )}
    >
      {label}
      <ArrowRight size={16} aria-hidden="true" />
    </Link>
  )
}

/**
 * The room's photographs, the uploaded cover added when it is a genuine extra
 * view. A picture listed twice is shown once.
 */
function roomPhotos(room: StudioRoomView): StudioPhoto[] {
  const cover = room.image ? [{ src: room.image, alt: room.imageAlt ?? room.name }] : []
  const pool = [...studioPhotos(room.slug), ...cover]
  return pool.filter((one, index) => pool.findIndex((other) => other.src === one.src) === index)
}
